/**
 * ColorPickerTile  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { BEM, COLOR } from "@adapptio/lib-components";
import { useColorPickerContext } from "./ColorPickerContext";
import { ColorPickerCheckboard } from "./ColorPickerCheckboard";
import { getColorUtils } from "./colorUtils";

export interface IColorPickerTileProps extends React.ComponentPropsWithRef<"div"> {
	className?: string;
	value?: string;
}

export const ColorPickerTile: React.FC<IColorPickerTileProps> = React.forwardRef(
	({ children, className, value, ...props }, ref) => {
		const { pickedColor } = useColorPickerContext();
		const baseClass = BEM("edt-color-picker-tile");

		const val = value || pickedColor;

		const color = useMemo(() => {
			if (COLOR[val]) {
				const computedStyle = window.getComputedStyle(document.documentElement);
				const cssValue = computedStyle.getPropertyValue(`--color-${COLOR[val]}`).trim();

				if (cssValue) {
					return cssValue;
				}
			}

			return val;
		}, [ val ]);

		const transparent = useMemo(() => getColorUtils(color).getAlpha() < 1, [ color ]);

		const colorStyle: React.CSSProperties = useMemo(
			() => ({
				backgroundColor: color
			}),
			[ color ]
		);

		if (className) {
			baseClass.add(className);
		}

		if (transparent || React.Children.count(children) > 0) {
			return (
				<div className={baseClass.toClassName()} ref={ref} {...props}>
					{children}
					<div className={baseClass.element("color").toClassName()} style={colorStyle} />
					{transparent && (
						<ColorPickerCheckboard className={baseClass.element("checkboard").toClassName()} />
					)}
				</div>
			);
		}

		return <div className={baseClass.toClassName()} ref={ref} style={colorStyle} {...props} />;
	}
);
